import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { LoggerService } from '../services/logger.service';
import { ScanCodeResolverService } from '../services/scan-code-resolver.service';
import { isNativeShell } from '../utils/native-shell';
import { appStoreLinkForBrowser } from '../utils/tag-link';

/**
 * /t/:code — a tag link opened outside the app's scan page. Shows the "get the app"
 * landing page only in a mobile browser with a store listing; otherwise goes straight
 * on to /t/:code/open.
 */
export const tagLinkLandingGuard: CanActivateFn = (route) => {
  if (!isNativeShell() && appStoreLinkForBrowser(navigator.userAgent)) return true;
  return inject(Router).createUrlTree(['/t', route.paramMap.get('code') ?? '', 'open']);
};

/**
 * /t/:code/open (after auth and sync init) — resolves the code like any scanned code and
 * redirects to its form. An unknown code lands on the scan page with an explanation.
 */
export const tagLinkOpenGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const log = inject(LoggerService).getLogger('tag-link');
  const code = (route.paramMap.get('code') ?? '').toUpperCase();

  const resolution = await inject(ScanCodeResolverService).resolve(code);
  if (!resolution.found) {
    log.info('Tag link code not found:', code);
    return router.createUrlTree(['/scan'], { queryParams: { tagNotFound: code } });
  }
  log.debug('Tag link resolved:', code, resolution.commands);
  return router.createUrlTree(resolution.commands, resolution.extras);
};
